import React from 'react';
import { defineMessages, t } from '@sm/intl';
import { createURL } from '@sm/utils';
import { Box } from '@wds/box';
import EndpageHeader from '~app/components/EndpageHeader';
import RebrandEpFormCore from '~app/components/RebrandEpFormCore';
import { ProductsCalloutListItem } from '~app/components/ProductsCalloutList/ProductsCalloutList';
import PerksIconWhite from '~static/images/icons/perks-icon-white.svg';
import MsgIconWhite from '~static/images/icons/msg-icon-white.svg';
import SparkleWhite from '~static/images/brazilExp/icons/sparkle-white.svg';
import { getSrc } from '~app/helpers/assetLoading';
import { RebrandV2IntlProps } from '~helpers/types';
import StaticThanksHeader from '~app/components/staticThanksHeader/StaticThanksHeader';
import { REBRANDV2_ENDPAGE_COPY } from '../StandardEndpage/copy';

const COPY = defineMessages({
  ENDPAGE_HEADER_COPY: {
    id: 'SurveyThanks.RebrandEpFormIntl.EndpageHeaderCopy',
    defaultMessage: 'Thank you for taking this survey.',
    description: '[Type: Header][Vis: med] - Informs the viewer that this survey has just completed',
  },
  PRODUCT_CALLOUT_PERKS_HEADING: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutPerksHeader',
    defaultMessage: 'Is your company missing key perks?',
    description:
      '[Type: Header][Vis: med] - Tagline to help entice users to views click the cta below. The CTA leads to another signup page with minimal information.',
  },
  PRODUCT_CALLOUT_PERKS_BODY: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutPerkseBody',
    defaultMessage:
      'People love their work perks. Send a survey to find out if you’re offering the incentives employees and potential candidates want.',
    description: '[Type: Paragraph][Vis: med] - Gives context to the above Tagline',
  },
  PRODUCT_CALLOUT_PERKS_CTA: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutPerksCTA',
    defaultMessage: 'Find out',
    description: '[Type: Button][Vis: med] - The text for a button that leads users to another sign up page',
  },
  PRODUCT_CALLOUT_COLLABORATION_HEADING: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutCollaborationEUHeader',
    defaultMessage: 'Are your customers satisfied?',
    description: '[Type: Header][Vis: med] - Tag line to help user learn more about SurveyMonkey Collaboration',
  },
  PRODUCT_CALLOUT_COLLABORATION_BODY: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutCollaborationEUBody',
    defaultMessage: '83% of businesses who describe themselves as “successful” actively measure customer satisfaction.',
    description: '[Type: Paragraph][Vis: med] - Expands and reiterate the above header line.',
  },
  PRODUCT_CALLOUT_COLLABORATION_CTA: {
    id: 'SurveyThanks.RebrandEpFormIntl.ProductCalloutCollaborationEUCTA',
    defaultMessage: 'Get started',
    description:
      "[Type: Button][Vis: med] - The button text which leads users to a marketing page to learn more about SurveyMonkey' premium plans",
  },
});

function RebrandEpFormIntl({
  treatment,
  utSource2,
  utSource3 = 'ProductCalloutItem',
}: {
  treatment: string;
  utSource2: string;
  utSource3?: string;
}): React.ReactElement {
  const utSource = 'survey-thanks';

  const CALLOUT_ITEM_SIGN_UP: ProductsCalloutListItem = {
    icon: getSrc(PerksIconWhite),
    heading: t(COPY.PRODUCT_CALLOUT_PERKS_HEADING),
    copy: t(COPY.PRODUCT_CALLOUT_PERKS_BODY),
    href: createURL('/user/sign-up/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: COPY.PRODUCT_CALLOUT_PERKS_CTA.defaultMessage,
    }),
    ctaText: COPY.PRODUCT_CALLOUT_PERKS_CTA,
    ctaUsage: 'sign-up secondary',
    ctaColor: 'sabaeus',
    name: 'perks',
  };

  const CALLOUT_ITEM_COLLABORATION: ProductsCalloutListItem = {
    icon: getSrc(MsgIconWhite),
    heading: t(COPY.PRODUCT_CALLOUT_COLLABORATION_HEADING),
    copy: t(COPY.PRODUCT_CALLOUT_COLLABORATION_BODY),
    href: createURL('/mp/collaboration/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: COPY.PRODUCT_CALLOUT_COLLABORATION_CTA.defaultMessage,
    }),
    ctaText: COPY.PRODUCT_CALLOUT_COLLABORATION_CTA,
    ctaUsage: 'collaboration',
    ctaColor: 'concord',
    name: 'collaboration',
  };

  const REBRANDV2_CALLOUT_ITEM: RebrandV2IntlProps = {
    icon: getSrc(SparkleWhite),
    formHeader: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_BRAZIL_FORM_CTA_HEADER),
    formSubHeader: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_INTL_FORM_CTA_SUBHEADER),
    formCalloutBody: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_PRODUCT_CALLOUT_BODY),
    brazilFormHeader: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_BRAZIL_FORM_CTA_HEADER),
    brazilFormSubHeader: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_BRAZIL_FORM_CTA_SUBHEADER),
    heading: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_PRODUCT_CALLOUT_COPY),
    href: createURL('/product/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: REBRANDV2_ENDPAGE_COPY.REBRANDV2_PRODUCT_CALLOUT_CTA.defaultMessage,
    }),
    signupHref: createURL('/user/sign-up/', {
      ut_source: utSource,
      ut_source2: utSource2,
      ut_source3: utSource3,
      ut_ctatext: REBRANDV2_ENDPAGE_COPY.REBRANDV2_BRAZIL_BRANDS_SIGNUP_CTA.defaultMessage,
    }),
    ctaText: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_PRODUCT_CALLOUT_CTA),
    brazilProductCalloutHeader: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_BRAZIL_PRODUCT_CALLOUT_HEADER),
    brazilctaSignupText: t(REBRANDV2_ENDPAGE_COPY.REBRANDV2_BRAZIL_BRANDS_SIGNUP_CTA),
    ctaUsage: 'explore the product',
    name: 'rebrandv2 product callout',
  };

  return (
    <>
      {treatment === 'rebrandEpFormIntlV2' ? (
        <Box style={{ backgroundColor: '#00BF6F' }}>
          <StaticThanksHeader utSource2={utSource2} />
        </Box>
      ) : (
        <EndpageHeader copy={t(COPY.ENDPAGE_HEADER_COPY)} hasLogo headerColor="sabaeus" headerType="thanks" />
      )}

      <RebrandEpFormCore
        utSource={utSource}
        utSource2={utSource2}
        calloutItem1={CALLOUT_ITEM_SIGN_UP}
        calloutItem2={CALLOUT_ITEM_COLLABORATION}
        calloutItem3={REBRANDV2_CALLOUT_ITEM}
        treatment={treatment}
      />
    </>
  );
}

export default RebrandEpFormIntl;
